import React, { useState, useEffect, useRef } from 'react';
import { translations } from '../../utils/translations';
import { useApp } from '../../context/AppContext';
import { printReceipt } from '../../utils/printReceipt';
import { Printer, AlertTriangle } from 'lucide-react';

const ReceiptModal = ({ receipt, onClose }) => {
  const { language, currentUser } = useApp();
  const t = translations[language];

  // 打印状态：结账成功、弹窗一打开，就自动尝试打印一次，不需要店员点任何按钮。
  // 注意：浏览器打印是弹出Windows打印对话框，代码没法知道店员最终点了
  // "打印"还是"取消"——所以这里的状态只能叫"已发送"，不能说成"打印成功"，
  // 这是浏览器打印功能本身的限制，不是没做好
  const [printStatus, setPrintStatus] = useState('idle'); // idle | printing | sent | failed
  const [printError, setPrintError] = useState('');
  const hasAutoPrinted = useRef(false);

  useEffect(() => {
    if (!receipt || hasAutoPrinted.current) return;
    hasAutoPrinted.current = true;
    doPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt]);

  const doPrint = async () => {
    setPrintStatus('printing');
    setPrintError('');
    try {
      await printReceipt(receipt, {
        storeName: currentUser?.store_name,
        cashierName: currentUser?.name,
      });
      setPrintStatus('sent');
    } catch (err) {
      console.error('打印小票失败:', err);
      setPrintStatus('failed');
      setPrintError(err.message || '未知错误');
    }
  };

  if (!receipt) return null;

  // 安全的数字格式化函数
  const formatMoney = (value) => {
    return parseFloat(value || 0).toFixed(2);
  };

  // ⚠️ 修复：这个弹窗之前一直在读 receipt.orderNo / receipt.total /
  // item.name / item.price 这几个驼峰命名的字段——但实际传进来的数据，
  // 不管是普通结账（saleApi.create返回的res.data）还是KHQR支付成功那条路
  // （handleKhqrSuccess里自己构造的对象），用的都是 order_no / total_amount /
  // product_name / unit_price 这套下划线命名，和系统里其他地方保持一致。
  // 两边字段名完全对不上，所有信息自然都是空的——这里统一改成正确的字段名，
  // 同时做了兼容兜底（两种命名都尝试），防止万一还有别的调用路径用了驼峰命名
  const orderNo = receipt.order_no || receipt.orderNo || '';
  const orderDate = receipt.created_at || receipt.date || '';
  const totalAmount = receipt.total_amount ?? receipt.total ?? 0;
  const items = receipt.items || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">{t.thankYou}</h3>
          <p className="text-gray-600">{orderNo}</p>
          <p className="text-sm text-gray-500">{orderDate}</p>
        </div>

        {/* 打印状态提示：浏览器打印没法确认最终结果，只能说"已发送"，
            不说"打印成功"——如实反映这个限制 */}
        {printStatus === 'printing' && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
            <Printer className="w-4 h-4 animate-pulse" />
            正在打开打印窗口...
          </div>
        )}
        {printStatus === 'sent' && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-4">
            <Printer className="w-4 h-4" />
            已发送到打印机
          </div>
        )}
        {printStatus === 'failed' && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">打印失败，小票没有出</p>
              <p className="text-xs text-red-500 mt-0.5">{printError}</p>
              <button onClick={doPrint} className="text-xs text-red-600 underline mt-1.5">
                点击重试
              </button>
            </div>
          </div>
        )}

        <div className="border-t border-b py-4 mb-4">
          {items.map((item, idx) => (
            <div key={idx} className="flex justify-between mb-2">
              <span className="text-gray-700">{item.product_name || item.name} × {item.quantity || 0}</span>
              <span className="font-medium">
                {t.yuan}{formatMoney((item.unit_price ?? item.price ?? 0) * (item.quantity || 0))}
              </span>
            </div>
          ))}
        </div>
        
        <div className="flex justify-between text-xl font-bold mb-6">
          <span>{t.total}:</span>
          <span className="text-blue-600">{t.yuan}{formatMoney(totalAmount)}</span>
        </div>

        <div className="flex gap-3">
          {/* 手动重打：小票打完了，客人要求再来一张，或者刚才打印失败重试成功了，
              都可能需要再打一次，不用重新走一遍结账流程 */}
          <button
            onClick={doPrint}
            disabled={printStatus === 'printing'}
            className="flex items-center justify-center gap-1.5 px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <Printer className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {t.confirm}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;